import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '../config/api';
import { db } from '../lib/database';

export const entityService = {
  /**
   * Fetch entities with optional filters
   * @param {Object} filters - filters like { lat, lng, radius, search, entity_type_id }
   */
  list: async (filters = {}) => {
    try {
      const { entity_type_id, ...otherFilters } = filters;
      let endpoint = API_ENDPOINTS.entities.list;
      let params = filters;

      if (entity_type_id) {
        endpoint = API_ENDPOINTS.entities.listByType(entity_type_id);
        params = otherFilters;
      }

      const response = await apiClient.get(endpoint, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching entities:', error);
      throw error;
    }
  },

  /**
   * Search entities by ID or Name
   * @param {string} query - Search query
   * @param {string} entityTypeId - Optional entity type ID
   */
  search: async (query, entityTypeId = null) => {
    return entityService.list({ q: query, entity_type_id: entityTypeId });
  },

  /**
   * Get nearest entities
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {number} radius - Radius in meters (default 200)
   * @param {string} entityTypeId - Optional entity type ID
   */
  getNearest: async (lat, lng, radius = 200, entityTypeId = null) => {
    return entityService.list({ 
      near: `${lat},${lng}`,
      entity_type_id: entityTypeId 
    });
  },

  /**
   * Create a new entity
   * @param {Object} data - Entity data
   */
  create: async (data) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.entities.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating entity:', error);
      throw error;
    }
  },

  /**
   * Get entity by ID
   * @param {string} id - Entity ID
   */
  getById: async (id) => {
    // Try local DB first if offline or if it's a temp ID
    if (!navigator.onLine || String(id).startsWith('temp_')) {
      const localEntity = await db.entities.get(String(id));
      if (localEntity) return { data: localEntity };
      if (!navigator.onLine) throw new Error('Entity not found locally and you are offline.');
    }

    try {
      const response = await apiClient.get(API_ENDPOINTS.entities.getById(id));
      return response.data;
    } catch (error) {
      // Fallback to local
      const localEntity = await db.entities.get(String(id));
      if (localEntity) return { data: localEntity };
      
      console.error('Error fetching entity:', error);
      throw error;
    }
  },

  /**
   * Update an existing entity
   * @param {string} id - Entity ID
   * @param {Object} data - Updated entity data
   */
  update: async (id, data) => {
    try {
      const response = await apiClient.put(API_ENDPOINTS.entities.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating entity:', error);
      throw error;
    }
  },

  /**
   * Get entity type details (schema)
   * @param {string} id - Entity type ID
   */
  getEntityType: async (id) => {
    // Try to get from local DB first if offline
    if (!navigator.onLine) {
      const localType = await db.entity_types.get(String(id));
      if (localType) return { data: localType };
      throw new Error('Entity type not found locally and you are offline.');
    }

    try {
      const response = await apiClient.get(`${API_ENDPOINTS.entities.types}/${id}`);
      const data = response.data?.data || response.data;
      
      // Cache for offline use
      if (data) {
        await db.entity_types.put({
          ...data,
          id: String(data.id)
        });
      }
      
      return response.data;
    } catch (error) {
      // Fallback to local if API fails
      const localType = await db.entity_types.get(String(id));
      if (localType) return { data: localType };
      
      console.error('Error fetching entity type:', error);
      throw error;
    }
  }
};
